/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatFollowersCount } from "@/Helpers/format-helper";
import { getRandomProfileEmoji } from "@/Helpers/helper";
import {
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Sheet,
} from "@/shadcnComponents/ui/sheet";
import {
  Heart,
  MessageCircle,
  Handshake,
  CheckCircleIcon,
  Loader,
  Coins,
} from "lucide-react";
import { Button } from "@/shadcnComponents/ui/button";
import { GrAdd } from "react-icons/gr";
import { LiaHandPointer } from "react-icons/lia";
import { DetailsPopup } from "./NewCampaignInfluencerDetailsPopup";
import { IoLocationOutline } from "react-icons/io5";
import { FaLinkedin } from "react-icons/fa";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/shadcnComponents/ui/skeleton";
import { TagChip } from "../../../LockedCreatorDetails/LockedCreatorDetails";
import {
  NoActivePlanAlertButton,
  SampleCampaignAlertButton,
  ToolTipInfo,
} from "../Components";
import { useState } from "react";
import {
  NewCampaignInfluencerProfile,
  NewCampaignMasterData,
} from "../../CampaignInterface/NewCampaignInterface";
import { Input } from "@/shadcnComponents/ui/input";
import { useBrand } from "@/Providers/Brand";
import { useParams } from "react-router-dom";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import { X } from "lucide-react";
import { Dialog, DialogContent } from "@/shadcnComponents/ui/dialog";
import { useAuth } from "@/Providers/Auth";
import { useNewCampaign } from "../../CustomHooks/NewCampaignHooks";
import { useDebouncedCallback } from "@/Helpers/useDebounce";

export const InfluencerCard = ({
  influencer,
  isSelected,
  masterData,
  onClick,
  onIgnore,
  index,
  collabType,
  onChangePrice,
}: {
  influencer: NewCampaignInfluencerProfile;
  isSelected: boolean;
  masterData: NewCampaignMasterData;
  onClick: () => Promise<void>;
  onIgnore?: () => Promise<void>;
  index: number;
  collabType: string;
  onChangePrice: (influencerId: string, price: number) => Promise<void>;
}) => {
  const { collabId } = useParams();
  const brandState = useBrand();
  const { toast } = useToast();
  const authState = useAuth();
  const { adminFeature } = useNewCampaign();
  const [isSelecting, setIsSelecting] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [price, setPrice] = useState(influencer?.priceByBrand);
  const [isLoading, setIsLoading] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const { formData } = useNewCampaign();

  const handleViewInfluencerDetails = async (influencerId: string) => {
    try {
      if (collabId === "sample") {
        setShowTokenModal(false);
        setIsSheetOpen(true);
        return;
      }

      const result = await brandState.handlePlanUsageFromClient({
        activity: "detailedInfluencerInsights",
        influencerId,
        collabId,
      });
      if (result?.success) {
        authState?.setReFetchBrandData((prev) => !prev);
        setShowTokenModal(false);
        setIsSheetOpen(true);
      } else {
        return toast({
          title: result.Error,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: error.message,
        variant: "destructive",
      });
      return { success: false };
    }
  };

  const checkViewInfluencerDetails = async (influencerId: string) => {
    try {
      const result = await brandState.checkPlanUsageFromClient({
        activity: "detailedInfluencerInsights",
        influencerId,
      });
      return result;
    } catch (error) {
      return { success: false };
    }
  };

  const debouncedPriceChange = useDebouncedCallback(
    (influencerId, newPrice) => {
      onChangePrice(influencerId, newPrice);
    },
    500
  );

  const handlePriceChange = (e) => {
    const newPrice = parseInt(e.target.value);
    setPrice(newPrice); // Update UI immediately
    debouncedPriceChange(influencer?._id, newPrice); // Debounced API call
  };

  return (
    <>
      {showTokenModal && (
        <UnlockModal
          open={showTokenModal}
          contentTitle="Unlock Influencer Insights"
          contentDescription="Gain full access to this influencer's detailed statistics"
          availableCredits={
            authState?.loggedBrand?.creditStats?.find(
              (item) => item?.type === "Insight Tokens"
            )?.available
          }
          creditCost={1}
          onUnlock={() => handleViewInfluencerDetails(influencer?._id)}
          onCancel={() => setShowTokenModal(false)}
        />
      )}
      <div
        key={influencer?._id}
        className={cn(
          "flex flex-col w-full items-end gap-3 self-stretch border bg-[color:var(--Shade-White,#FFF)] p-4 rounded-[10px] border-solid border-[#E7E7E7]",
          isSelected ? "border-[#28BE28]" : "border-[#E7E7E7]"
        )}
      >
        {/* top card */}
        <div className="flex justify-between items-start self-stretch">
          {/* top left card */}
          <div className=" flex w-[422px] flex-col justify-center items-start gap-3">
            {/* badge */}
            <div className="flex items-start gap-3 w-full">
              {influencer?.badge && (
                <div className="flex justify-center items-center gap-[5.144px] bg-[rgba(17,126,184,0.12)] px-2.5 py-1.5 rounded-[5.144px] text-[#117EB8] text-xs not-italic font-semibold leading-[normal]">
                  {influencer?.badge}
                </div>
              )}
              {influencer?.CPI > 0 && adminFeature === "true" && (
                <div className="flex justify-center items-center gap-[5.144px] bg-[rgba(17,126,184,0.12)] px-2.5 py-1.5 rounded-[5.144px] text-[#117EB8] text-xs not-italic font-semibold leading-[normal]">
                  CPI - {influencer?.CPI}
                </div>
              )}
              {influencer?.algoImpressions > 0 && adminFeature === "true" && (
                <div className="flex justify-center items-center gap-[5.144px] bg-[rgba(17,126,184,0.12)] px-2.5 py-1.5 rounded-[5.144px] text-[#117EB8] text-xs not-italic font-semibold leading-[normal]">
                  Impressions - {influencer?.algoImpressions}
                </div>
              )}
              {influencer?.maxExpense > 0 && adminFeature === "true" && (
                <div className="flex justify-center items-center gap-[5.144px] bg-[rgba(17,126,184,0.12)] px-2.5 py-1.5 rounded-[5.144px] text-[#117EB8] text-xs not-italic font-semibold leading-[normal]">
                  maxExpense - {influencer?.maxExpense}
                </div>
              )}
              {influencer?.FinalExpense > 0 && adminFeature === "true" && (
                <div className="flex justify-center items-center gap-[5.144px] bg-[rgba(17,126,184,0.12)] px-2.5 py-1.5 rounded-[5.144px] text-[#117EB8] text-xs not-italic font-semibold leading-[normal]">
                  FinalExpense - {influencer?.FinalExpense}
                </div>
              )}
            </div>
            {/* influencer details */}
            <div className="flex items-start gap-2.5 self-stretch">
              {/* influencer image */}
              <div className="overflow-hidden w-[50px] h-[50px]  rounded-[4px]  ">
                <img
                  className="w-[50px] h-[50px] cover "
                  src={influencer?.profile}
                  alt=""
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onError={(e: any) => {
                    const randomProfileEmoji = getRandomProfileEmoji();
                    e.target.onerror = null;
                    e.target.src = randomProfileEmoji;
                  }}
                />
              </div>
              {/* influencer info */}
              <div className="flex flex-col justify-center items-start gap-3.5 flex-[1_0_0]">
                {/* name and tagline */}
                <div className="flex flex-col justify-center items-start gap-1 self-stretch">
                  <p className="text-[color:var(--Shade-Black,#000)] text-base not-italic font-semibold leading-[normal]">
                    {influencer?.name}
                  </p>
                  {influencer?.tagline && (
                    <div className="text-slate-500 text-sm not-italic font-normal leading-[18px] line-clamp-2 ">
                      {influencer?.tagline}
                    </div>
                  )}
                </div>
                {/* linkedin and location */}
                <div className="flex justify-center items-center gap-5">
                  <div className="flex justify-center items-center gap-1">
                    <span
                      className="cursor-pointer"
                      onClick={() => {
                        if (!/^https?:\/\//i.test(influencer?.linkedinLink)) {
                          const link = "https://" + influencer?.linkedinLink;
                          return window.open(link, "_blank");
                        }
                        window?.open(influencer?.linkedinLink, "_blank");
                      }}
                    >
                      <FaLinkedin size={14} color="#0091FF" />
                    </span>
                    <span className="text-[#252525] text-xs not-italic font-semibold leading-[normal]">
                      {formatFollowersCount(influencer?.cleanedFollowers)}
                    </span>
                  </div>
                  {influencer?.location && (
                    <div className="flex gap-1 items-center text-slate-500 text-xs not-italic font-normal leading-[normal]">
                      <IoLocationOutline size={14} color="#0091FF" />
                      <span>{influencer?.location}</span>
                    </div>
                  )}
                </div>
                {influencer?.ScrapedCategory &&
                  influencer?.ScrapedCategory?.length > 0 && (
                    <div className="flex w-full items-start gap-[8px] flex-wrap">
                      {influencer?.ScrapedCategory?.map((category, index) => (
                        <TagChip
                          className="py-1 px-2.5 font-[400] "
                          key={index}
                          text={`${
                            masterData?.ContentCategory?.find(
                              (Cc) => Cc?._id == category
                            )?.categoryName || category
                          }`}
                          variant={"colored"}
                        />
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </div>
          {/* top right card */}
          <div className="flex w-[280px] flex-col gap-10 items-start">
            <div className="flex items-start gap-3 w-full">
              {influencer?.isSampleInfluencer && (
                <div className="flex justify-center w-full items-center gap-[5.144px] bg-[rgba(17,126,184,0.12)] px-2.5 py-1.5 rounded-[5.144px] text-[#117EB8] text-sm not-italic font-semibold leading-[normal]">
                  {"Dummy Profile"}
                </div>
              )}
            </div>
            <div className="flex w-[280px] justify-between items-start">
              {[
                {
                  id: 0,
                  icon: <LiaHandPointer size={24} color="#FF8A8A" />,
                  key: "Avg. ER",
                  value: influencer?.LinkedinAnalysis?.avgEngagementPercentage
                    ? `${
                        Math.ceil(
                          influencer?.LinkedinAnalysis
                            ?.avgEngagementPercentage * 10
                        ) / 10
                      }%`
                    : 0,
                  toolTipInfo:
                    "Engagement Rate is the percentage of followers engaging",
                },
                {
                  id: 1,
                  icon: <Heart size={20} color="#FF8A8A" />,
                  key: "Avg. Likes",
                  value: influencer?.LinkedinAnalysis?.avgLikes
                    ? formatFollowersCount(
                        Math.ceil(influencer?.LinkedinAnalysis?.avgLikes)
                      )
                    : 0,
                },
                {
                  id: 2,
                  icon: <MessageCircle size={20} color="#FF8A8A" />,
                  key: "Avg. Comments",
                  value: influencer?.LinkedinAnalysis?.avgComments
                    ? formatFollowersCount(
                        Math.ceil(influencer?.LinkedinAnalysis?.avgComments)
                      )
                    : 0,
                },
                {
                  id: 3,
                  icon: <Handshake size={20} color="#FF8A8A" />,
                  key: "Collabs",
                  value: influencer?.collab_count
                    ? formatFollowersCount(Math.ceil(influencer?.collab_count))
                    : 0,
                  toolTipInfo:
                    "Number of past collaborations the influencer has done",
                },
              ]?.map((item, index) => {
                if (item.value === 0) return null;
                return (
                  <div
                    key={index}
                    className=" flex  flex-col items-center gap-1"
                  >
                    <span className="text-[color:var(--Shade-Black,#000)] text-center text-base not-italic font-semibold leading-[normal]">
                      {item.value}
                    </span>
                    <span className="text-[#9C9C9C] flex gap-1 items-center text-[10px] not-italic font-semibold leading-[normal]">
                      {item.key}{" "}
                      {item?.toolTipInfo && (
                        <ToolTipInfo Content={item?.toolTipInfo} size={10} />
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* select price */}
        {collabType === "fixed" && (
          <div className="flex gap-6 self-stretch pl-[60px]">
            {influencer?.recommendedPrice > 3000 && (
              <div className="flex flex-col items-start gap-2 flex-[1_0_0] px-3.5 py-3 rounded-sm  border bg-[var(#FFFFFF)] border-solid border-[#F0F3F8]">
                <span className="flex justify-center items-center bg-[linear-gradient(94deg,rgba(255,160,69,0.08)_-6.81%,rgba(188,56,203,0.08)_43.08%,rgba(66,140,251,0.08)_95%)] px-2.5 py-1.5 rounded-[5.144px] text-xs not-italic font-semibold leading-[normal]">
                  Recommended Commercials
                </span>{" "}
                <span className="text-[var(#020617)] text-base not-italic font-bold leading-[normal] tracking-[-0.24px]">
                  ₹{influencer?.recommendedPrice?.toLocaleString()}
                </span>{" "}
              </div>
            )}
            <div className="flex flex-col items-start gap-2 flex-[1_0_0] px-3.5 py-3 rounded-sm  border bg-[var(#FFFFFF)] border-solid border-[#F0F3F8]">
              <span className="text-[var(#020617)] text-base not-italic font-bold leading-[normal] tracking-[-0.24px]">
                {!isSelected
                  ? "Select to Set Custom Commercials"
                  : "Enter Custom Commercials"}
              </span>
              <Input
                onChange={handlePriceChange}
                value={price}
                disabled={!isSelected}
                type="number"
                min={100}
                placeholder="Eg., 4500"
              />
            </div>
          </div>
        )}

        {/* bottom card */}
        <div className="flex justify-between items-center self-stretch pl-[60px]">
          <div className="flex items-start gap-5 px-0 py-2.5 text-[#7D7D7D] text-xs not-italic font-medium leading-[normal] underline decoration-solid decoration-auto underline-offset-auto">
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <div
                  onClick={async (e) => {
                    e.preventDefault();
                    setIsLoading(true);
                    try {
                      const result = await checkViewInfluencerDetails(
                        influencer?._id
                      );
                      if (result?.success) {
                        setIsSheetOpen(true);
                      } else {
                        setShowTokenModal(true);
                      }
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  className="flex cursor-pointer gap-2 items-center justify-center"
                >
                  <span>
                    {isLoading ? "Loading..." : "View Detailed Analysis"}
                  </span>
                  {isLoading && (
                    <Loader size={12} className="animate-spin ml-1" />
                  )}
                </div>
              </SheetTrigger>
              <SheetContent className="p-0 m-0 w-[400px] sm:min-w-[70%] sm:max-w-max text-white bg-[#FBFBFB]">
                <SheetHeader className="hidden">
                  <SheetTitle></SheetTitle>
                  <SheetDescription></SheetDescription>
                </SheetHeader>
                {/* <SheetClose asChild>
                  <div className="fixed right-[71%] top-3 px-2 py-1 bg-black/70 rounded-md text-xs cursor-pointer">
                    X
                  </div>
                </SheetClose> */}
                <DetailsPopup
                  influencer={influencer}
                  masterData={masterData}
                  isSelected={isSelected}
                  onClick={async () => {
                    const shouldWorkNormally =
                      !formData?.campaignInfo?.isSampleCampaign ||
                      (formData?.campaignInfo?.isSampleCampaign &&
                        influencer?.isSampleInfluencer);
                    if (!shouldWorkNormally) return;
                    await onClick();
                    setIsSheetOpen(false);
                  }}
                  index={index}
                />
              </SheetContent>
            </Sheet>
          </div>
          <div className="flex  items-start gap-3">
            {adminFeature === "true" && influencer?.AllowRequestData && (
              <Button
                onClick={async () => {
                  setIsRequesting(true);
                  const response =
                    await brandState?.requestLatestDataFromInfluencer({
                      influencerId: influencer?._id,
                      collabId,
                    });

                  if (response?.Error) {
                    toast({
                      title: "Error",
                      description: response?.Error,
                      variant: "destructive",
                    });
                    setIsRequesting(false);
                    return;
                  }

                  toast({
                    title: "Request Sent",
                    description: `Request for latest data sent to ${influencer?.name}`,
                    variant: "default",
                  });
                  setIsRequesting(false);
                }}
                className="px-10 py-2.5 text-[#121212] text-xs not-italic font-medium leading-[11.574px] tracking-[-0.322px] border border-[color:var(--Shade-Black,#000)] rounded-[5.144px] border-solid"
                variant="outline"
              >
                {isRequesting ? (
                  <Loader className="animate-spin " />
                ) : (
                  "Request Data"
                )}
              </Button>
            )}

            {onIgnore && (
              <Button
                onClick={async () => {
                  // setPrice(0);
                  setIsSelecting(true);
                  isSelected ? await onClick() : await onIgnore();
                  setIsSelecting(false);
                }}
                className="px-10 py-2.5 text-[#121212] text-xs not-italic font-medium leading-[11.574px] tracking-[-0.322px] border border-[color:var(--Shade-Black,#000)] rounded-[5.144px] border-solid"
                variant="outline"
              >
                {isSelecting ? (
                  <Loader className="animate-spin " />
                ) : isSelected ? (
                  "Deselect"
                ) : (
                  "Ignore"
                )}
              </Button>
            )}
            <SampleCampaignAlertButton
              Button={
                <NoActivePlanAlertButton
                  Button={
                    <Button
                      variant={isSelected ? "secondary" : "destructive"}
                      id={`search_creator_${index}`}
                      className={`gap-2 px-10 py-2.5 text-[color:var(--Base-White,var(--Shade-White,#FFF))] text-xs not-italic font-medium leading-[11.574px] tracking-[-0.322px] ${
                        isSelected
                          ? "text-[#28BE28] bg-[#E6FFD5] "
                          : "text-white"
                      } tracking-wider`}
                    >
                      {isSelecting ? (
                        <Loader className="animate-spin " />
                      ) : (
                        <>
                          <span>{isSelected ? "Selected" : "Select"}</span>
                          {isSelected ? (
                            <CheckCircleIcon size={14} />
                          ) : (
                            <GrAdd size={12} />
                          )}
                        </>
                      )}
                    </Button>
                  }
                  onClick={async () => {
                    const shouldWorkNormally =
                      !formData?.campaignInfo?.isSampleCampaign ||
                      (formData?.campaignInfo?.isSampleCampaign &&
                        influencer?.isSampleInfluencer);
                    if (!shouldWorkNormally) return;
                    setIsSelecting(true);
                    await onClick();
                    setIsSelecting(false);
                  }}
                />
              }
              isSampleInfluencer={influencer?.isSampleInfluencer}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export const InfluencerCardSkeleton = () => {
  return (
    <div className="flex flex-col w-full items-end gap-3 self-stretch  bg-[color:var(--Shade-White,#E7E7E710)] p-4 rounded-[10px] border border-solid border-[#E7E7E7]">
      <div className="flex justify-between items-center self-stretch">
        {/* top left card */}
        <div className=" flex w-[422px] flex-col justify-center items-start gap-3">
          {/* badge */}
          <div className="flex items-start gap-3">
            <Skeleton className="h-6 w-20 " />
          </div>
          {/* influencer details */}
          <div className="flex items-start gap-2.5 self-stretch">
            {/* influencer image */}
            <Skeleton className="w-[50px] h-[50px] rounded-[4px] " />
            {/* influencer info */}
            <div className="flex flex-col justify-center items-start gap-3.5 flex-[1_0_0]">
              {/* name and tagline */}
              <div className="flex flex-col justify-center items-start gap-1 self-stretch">
                <Skeleton className="w-[64px] h-[20px] " />
                <Skeleton className="w-[160px] h-[20px] " />
              </div>
            </div>
          </div>
        </div>

        {/* top right card */}
        <div className="flex w-[280px] justify-between items-start">
          {[0, 1, 2, 3]?.map((_item, index) => {
            return (
              <div key={index} className=" flex  flex-col items-center gap-1">
                <Skeleton className="w-[50px] h-[50px] rounded-[4px] " />
              </div>
            );
          })}
        </div>
      </div>
      {/* bottom card */}
      <div className="flex justify-between items-center self-stretch pl-[60px]">
        <div className="flex w-[100px] justify-between items-center gap-2.5">
          <Skeleton className="w-[120px] h-[15px] rounded-[4px] " />
        </div>
        <div className="flex  items-start gap-3">
          <Skeleton className="w-[120px] h-[40px] rounded-[4px] " />
          <Skeleton className="w-[120px] h-[40px] rounded-[4px] " />
        </div>
      </div>
    </div>
  );
};

interface UnlockModalProps {
  contentTitle: string;
  contentDescription: string;
  availableCredits: number;
  creditCost: number;
  onUnlock: () => void;
  onCancel: () => void;
  open: boolean;
}

export const UnlockModal = ({
  contentTitle = "Financial Forecast",
  contentDescription = "Gain full access to this influencer's detailed statistics",
  availableCredits = 4,
  creditCost = 1,
  onUnlock = () => {},
  onCancel = () => {},
  open,
}: UnlockModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleUnlock = async () => {
    setIsLoading(true);
    try {
      await onUnlock();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-md p-0 gap-0 border rounded-lg overflow-hidden">
        <div className="p-6 relative">
          <button
            onClick={onCancel}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>

          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">
                {contentTitle}
              </h2>
              <p className="text-muted-foreground mt-1">{contentDescription}</p>
            </div>

            <div className="bg-muted/50 p-4 rounded-md flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="text-amber-500">
                  <Coins className="h-5 w-5" />
                </div>
                <span className="font-medium">Available Insight Tokens</span>
              </div>
              <span className="text-xl font-bold">{availableCredits}</span>
            </div>

            <div className="border rounded-md p-4">
              Unlocking an influencer's insights costs <b>{creditCost}</b>{" "}
              Insight Token, but only once. Future access is free.
            </div>

            <div className="space-y-2">
              <Button
                className="w-full bg-black hover:bg-black/90 text-white"
                onClick={handleUnlock}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Unlocking...
                  </>
                ) : (
                  <>Unlock for {creditCost} Insight Tokens</>
                )}
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
